import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { ConcessionStatus, FacilityFormType, InstallationFlowType, TechnicianFormType, UserRoles } from '../constants/app.constants';
import { ApplicationResponseData, KeyValueData, TechnicianData } from '../domains/common.domain';
import { LocalStorageService } from '../services/local-storage.service';
import { NavigationStateShareService } from '../services/navigation-state-share.service';
import { PanelService } from './panel.service';
import { PanelData } from './panel.domain';
import { EnvironmentService } from '../services/environment.service';
import { RegistrationService } from '../registration/registration.service';
import { ErrorService } from '../services/error/error.service';
import { Observable } from 'rxjs';
import { CommonService } from '../services/common.service';
import { SkStatus } from '../+shared/enums/skStatus';
import { ConfirmationModalService } from '../+shared/components/confirmation-modal/confirmation-modal.service';
import { NetzTranslateService } from '../services/netz-translate.service';
import { Panel } from '../interface/config.interface';
import { DrrCcdDeviceApplicationDto } from '../+drr-ccd-devices/technical-data/technical-data.domain';

type AccessToApplicationsStatus = 'noaccess' | 'active' | 'expired' | 'newRegistrationSubmitted' | 'waitingForApproval';

@Component({
  providers: [PanelService],
  selector: 'app-panel',
  templateUrl: './panel.component.html',
  styleUrls: ['./panel.component.scss'],
})
export class PanelComponent implements OnInit {
  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    const focusable = Array.from(document.querySelectorAll('.tiles-padding')) as HTMLElement[];
    const index = focusable.findIndex(el => el === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        const next = focusable[index + 1] || focusable[0];
        next.focus();
        break;

      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        const focusableMenuItems = Array.from(document.querySelectorAll('.menu-item')) as HTMLElement[];
        const prev = focusable[index - 1] || focusableMenuItems[0];
        prev?.focus();
        break;

      case 'Enter':
        event.preventDefault();
        const id = focusable[index]?.id || null;
        this.selectProduct(id);
        break;
    }
  }

  allPanels: Panel[];
  installationFlowType = InstallationFlowType;
  isCustomer: boolean;
  role: string;
  isLoading: boolean;
  technicianData: TechnicianData;
  config: any;
  applicationStatus: AccessToApplicationsStatus;
  productsConfiguration: {};
  installationItems = [
    {
      id: 'new-installation',
      configKey: 'new-installation',
      icon: 'icon-zaehlereinbau.png',
      altText: 'zaehlereinbau',
      translationKey: 'installation-data.new-installation',
      isDisabled: false,
    },
    {
      id: 'update-installation',
      configKey: 'update-installation',
      icon: 'icon-zaehler-aenderung.png',
      altText: 'zaehler aenderung',
      translationKey: 'installation-data.update-installation',
      isDisabled: false,
    },
    {
      id: 'mains-connection',
      configKey: 'mains-connection',
      icon: 'icon-netzanschluss.png',
      altText: 'netzanschluss',
      translationKey: 'installation-data.mains-connection',
      isDisabled: false,
    },
    {
      id: 'feed-in-system',
      configKey: 'feed-in-system',
      icon: 'icon-einspeisung.png',
      altText: 'einspeisung',
      translationKey: 'installation-data.feed-in-system',
      isDisabled: false,
    },
    {
      id: 'temporary-connection',
      configKey: 'temporary-connection',
      icon: 'icon-zeitl-anschluss.png',
      altText: 'zeitl. Anschluss',
      translationKey: 'installation-data.temporary-connection',
      isDisabled: false,
    },
    {
      id: 'site-electricity-meterdata',
      configKey: 'site-electricity-meterdata',
      icon: 'icon-zeitl-anschluss.png',
      altText: 'zeitl. Anschluss',
      translationKey: 'installation-data.site-electricity-meterdata',
      get isDisabled() {
        return this.technicianData?.concession_power === '0';
      },
    },
    {
      id: 'change-house-connection',
      configKey: 'change-house-connection',
      icon: 'icon-netzanschluss-aenderung.png',
      altText: 'netzanschluss aenderung',
      translationKey: 'installation-data.change-house-connection',
      isDisabled: false,
    },
    {
      id: 'conversion-to-gas',
      configKey: 'conversion-to-gas',
      icon: 'icon-gas-fernwaerme.png',
      altText: 'gas u. fernwaerme',
      translationKey: 'installation-data.conversion-to-gas',
      isDisabled: false,
    },
    {
      id: 'grid-compatibility',
      configKey: 'grid-compatibility',
      icon: 'icon-grid-comp.png',
      altText: 'gas u. fernwaerme',
      translationKey: 'installation-data.grid-comp',
      isDisabled: false,
    },
    {
      id: 'reg-ep',
      configKey: 'reg-ep',
      icon: 'icon-einspeisung.png',
      altText: 'einspeisung',
      translationKey: 'installation-data.reg-ep',
      isDisabled: false,
    },
    {
      id: 'ccd-drr',
      configKey: 'drr-ccd-devices',
      icon: 'icon-ccd-drr-devices.png',
      altText: 'Anmeldepflichtige Geräte',
      translationKey: 'installation-data.ccd-drr-tile',
      isDisabled: false,
    },
  ];

  constructor(
    private route: Router,
    private localStorageService: LocalStorageService,
    private dataShare: NavigationStateShareService,
    private environmentService: EnvironmentService,
    private registrationService: RegistrationService,
    private errorService: ErrorService,
    private commonService: CommonService,
    private confirmationModalService: ConfirmationModalService,
    private netzTranslateService: NetzTranslateService
  ) {}

  ngOnInit(): void {
    this.role = this.localStorageService.getItem('role');
    this.isCustomer = this.role === UserRoles.customer;
    this.allPanels = this.environmentService.allPanels;
    this.productsConfiguration = this.allPanels.reduce((aggregated, p) => {
      aggregated[p.id] = { show: p.display && (!p.roles || p.roles.includes(this.role)) };
      return aggregated;
    }, {});

    this.isLoading = false;
    this.removeItemsFromStorage();
    if (!this.isCustomer) {
      this.getTechnicianApplication();
    }

    this.config = this.localStorageService.getItem('config');
  }

  public selectProduct(id) {
    const flowMap = {
      'new-installation': this.installationFlowType.NewInstallation,
      'update-installation': this.installationFlowType.UpdateInstallation,
      'mains-connection': this.installationFlowType.MainsConnection,
      'feed-in-system': this.installationFlowType.FeedInSystem,
      'temporary-connection': this.installationFlowType.TemporaryConnection,
      'site-electricity-meterdata': this.installationFlowType.SiteElectricityMeterdata,
      'change-house-connection': this.installationFlowType.ChangeHouseConnection,
      'conversion-to-gas': this.installationFlowType.ConversionToGas,
      'grid-compatibility': this.installationFlowType.GridCompatibility,
      'reg-ep': this.installationFlowType.RegEP,
      'ccd-drr': this.installationFlowType.ConsumptionDevices,
    };

    if (flowMap[id]) {
      this.redirectTo(flowMap[id]);
    }
  }

  private redirectTo(flowType: string): void {
    this.localStorageService.setItem('original_flow', flowType);
    const continueToNewApplication = () => {
      this.localStorageService.removeItem('application_id');

      if (!this.commonService.takeFromRequestOne) {
        this.localStorageService.removeItem('area_locations');
        this.localStorageService.removeItem('location_data');
      }

      this.continueToApplication(flowType);
    };

    const continueToExistingApplication = applicationId => {
      this.localStorageService.setItem('application_id', applicationId);
      this.continueToApplication(flowType);
    };

    this.getExistingApplicationId(flowType).then(applicationId => {
      if (applicationId) {
        this.confirmationModalService.show({
          message: () => this.netzTranslateService.translateString('basic-profile.jurisdiction.new-application-confirm-text'),
          accept: () => continueToNewApplication(),
          reject: () => {
            continueToExistingApplication(applicationId);
          },
        });
      } else {
        continueToNewApplication();
      }
    });
  }

  private continueToApplication(flowType: string) {
    if (
      (flowType === this.installationFlowType.SiteElectricityMeterdata && this.technicianData?.concession_power === '1') ||
      flowType !== this.installationFlowType.SiteElectricityMeterdata
    ) {
      this.localStorageService.setItem('installation_flow', flowType);
      this.dataShare.setFormState(1);
      this.route.navigate(['/jurisdiction']);
    }
  }

  removeItemsFromStorage(): void {
    this.localStorageService.removeItem('installation_flow');
    this.localStorageService.removeItem('original_flow');
    this.localStorageService.removeItem('selected_facilities');
    this.localStorageService.removeItem('selected_building_type');
    this.localStorageService.removeItem('documents_data');
    this.localStorageService.removeItem('selected_change_type');
    this.localStorageService.removeItem('site_electricity_required');
    this.localStorageService.removeItem('installer_id');
    // this.localStorageService.removeItem('area_locations');
    FacilityFormType.forEach(item => {
      this.localStorageService.removeItem(`${item.key}_facility_data`);
    });
    TechnicianFormType.forEach(item => {
      this.localStorageService.removeItem(`${item.key}_technician_data`);
    });
  }

  showChangeOfHouseConnection(item: PanelData): boolean {
    return (
      (!this.isCustomer || this.environmentService.showChangeHouseConnectionForCustomer) &&
      item.id === 'change-house-connection' &&
      item.display
    );
  }

  showFeedInSystemForCustomer(item: PanelData): boolean {
    return (!this.isCustomer || this.environmentService.showFeedInSystemForCustomer) && item.id === 'feed-in-system' && item.display;
  }

  showRegepTile(item: PanelData): boolean {
    return (!this.isCustomer || this.environmentService.showRegepForCustomer) && item.id === 'reg-ep' && item.display;
  }

  private getExistingApplicationId(installationFlow: string): Promise<string> {
    return new Promise((resolve, reject) => {
      let applicationSubscription: Observable<any>;
      let flowType: number;
      if (
        installationFlow === this.installationFlowType.NewInstallation ||
        installationFlow === this.installationFlowType.UpdateInstallation ||
        installationFlow === this.installationFlowType.FeedInSystem ||
        installationFlow === this.installationFlowType.SiteElectricityMeterdata
      ) {
        flowType = 1;
        applicationSubscription = this.commonService.getExistingInstallationApplications(installationFlow);
      } else if (
        installationFlow === this.installationFlowType.MainsConnection ||
        installationFlow === this.installationFlowType.TemporaryConnection ||
        installationFlow === this.installationFlowType.ChangeHouseConnection ||
        installationFlow === this.installationFlowType.ConversionToGas
      ) {
        flowType = 2;
        applicationSubscription = this.commonService.getExistingGridConnectionApplication(installationFlow);
      } else if (installationFlow === this.installationFlowType.GridCompatibility) {
        flowType = 3;
        applicationSubscription = this.commonService.getExistingGridCompFeedInApplications(installationFlow);
      } else if (installationFlow === this.installationFlowType.RegEP) {
        flowType = 4;
        applicationSubscription = this.commonService.getExistingRegepApplications(SkStatus.created);
      } else if (installationFlow === this.installationFlowType.ConsumptionDevices) {
        flowType = 5;
        applicationSubscription = this.commonService.getExistingConsumptionDeviceApplications(SkStatus.created);
      }

      this.isLoading = true;

      if (installationFlow === this.installationFlowType.ConsumptionDevices) {
        applicationSubscription.subscribe(
          (response: DrrCcdDeviceApplicationDto[]) => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(response, true);

            const existingApplication = response.sort(
              (a, b) => parseInt(a.application_id.toString()) - parseInt(b.application_id.toString())
            )[0];

            return resolve(existingApplication?.application_id.toString());
          },
          error => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        applicationSubscription.subscribe(
          (response: any) => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(response, true);
            /*
            (response as GetInstallationApplicationObject).installationapplication &&
              (response as GetInstallationApplicationObject).installationapplication.length) ||
              (response[0] as GridCompFeedInApplicationData)?.technical_data ||
              (response[0] as RegistrationOfEnergyProducerApplication)?.technical_data ||
              ((response as GetGridConnectionObject).gridconapplication && (response as GetGridConnectionObject).gridconapplication.length))
            */

            const existingApplication =
              (response.installationapplication && response.installationapplication[0]) ??
              response[0] ??
              response.technical_data ??
              (response.gridconapplication && response.gridconapplication[0]);

            return resolve(existingApplication?.application_id);
          },
          error => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      }
    });
  }

  private getTechnicianApplication() {
    const technicianId = this.localStorageService.getItem('technician_id');

    this.isLoading = true;
    this.registrationService.getTechnicianApplication(technicianId, 'technician_id').subscribe(
      (response: ApplicationResponseData) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.applicationStatus = this.checkApplicationStatus(response);
        this.technicianData = response.technician_application[0]?.technician[0];
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, false);
      }
    );
  }

  private checkApplicationStatus(response: ApplicationResponseData): AccessToApplicationsStatus {
    if (this.isCustomer) return 'active';

    if (!response?.technician_application?.length) return 'noaccess';
    const applicationStatus = response.technician_application[0].status;

    if (applicationStatus == ConcessionStatus.expired) {
      return 'expired';
    } else if (applicationStatus == ConcessionStatus.submitted) {
      return 'newRegistrationSubmitted';
    } else if ([ConcessionStatus.renew_requested].includes(applicationStatus)) {
      return 'waitingForApproval';
    } else {
      return 'active';
    }
  }
}
